/**
 * Tracking for client.web.admin.
 *
 * ONE sink: Rybbit. This is an internal operations console, so the three things
 * that used to be here are all gone:
 *
 *   amplitude-js  — the account is deactivated and the SDK is deprecated.
 *   react-ga      — v2, which speaks Universal Analytics. Google stopped
 *                   processing UA on 1 July 2023, and the library cannot accept
 *                   a GA4 id anyway. Every call has gone nowhere for three
 *                   years, which is why Amplitude was the only live analytics
 *                   this app had.
 *   react-facebook-pixel — an advertising pixel on a staff-only tool. It was
 *                   reporting internal CRUD to Facebook.
 *
 * Pageview tracking is also gone: Rybbit derives pageviews from the URL, so the
 * 33 hand-written trackPage calls double-counted every route.
 */

import { TRACKING_RYBBIT_HOST, TRACKING_RYBBIT_SITE_ID } from '../../../config/tracking.config';
import type { EventMap, EventName, UserRole } from './events';
import { rybbitIdentify, rybbitReset, rybbitTrack, setGlobalProperties } from './rybbit';

export type { EventName, EventMap } from './events';
export type { TrackingDescriptor } from './events';

const resolveEnv = (): 'production' | 'staging' =>
  process.env.REACT_APP_APP_ENV === 'production' ? 'production' : 'staging';

export const initializeTrackingTools = () => {
  try {
    if (!TRACKING_RYBBIT_SITE_ID) {
      // No id means the script is never injected and tracking is a no-op. There
      // is deliberately no fallback: Amplitude's key had one, so a misconfigured
      // environment wrote silently into the production project.
      return;
    }
    setGlobalProperties({ app: 'admin', env: resolveEnv(), user_role: 'staff' });

    // CRA has no next/script, so the tag is appended here. Guarded by id so a
    // re-render or a second init cannot load the tracker twice, which would
    // double every event — the same class of bug as the Button firing on three
    // DOM events.
    const SCRIPT_ID = 'rybbit-tracker';
    if (typeof document !== 'undefined' && !document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `${TRACKING_RYBBIT_HOST}/api/script.js`;
      script.defer = true;
      script.setAttribute('data-site-id', TRACKING_RYBBIT_SITE_ID);
      document.head.appendChild(script);
    }
  } catch (error) {
    console.error('Error initializing tracking tools:', error);
  }
};

/**
 * `roles` is accepted because the five existing call sites pass it, but only the
 * primary role becomes a property — a list would make every split ambiguous.
 */
export const identifyUser = (userId: string, role?: string) => {
  try {
    rybbitIdentify(userId);
    setGlobalProperties({ user_role: (role as UserRole) || 'staff' });
  } catch (error) {
    console.error('Error identifying user:', error);
  }
};

export const resetUser = () => {
  try {
    rybbitReset();
    setGlobalProperties({ user_role: 'anonymous' });
  } catch (error) {
    console.error('Error resetting user:', error);
  }
};

/**
 * The one way to record a product event. `event` is constrained to the taxonomy
 * and `properties` to that event's own shape, so a typo or a missing required
 * property is a build error rather than a new name in a dashboard.
 */
export const trackEvent = <E extends EventName>(event: E, properties: EventMap[E]) => {
  try {
    rybbitTrack(event, properties);
  } catch (error) {
    console.error('Error tracking event:', event, error);
  }
};
